import React from "react";
import { Button, FormControl, Alert } from "react-bootstrap";
import { DisplayBox } from "../../components";
import { ApiHelper, ArrayHelper, PendingCommand, PlaylistMessageConditionInterface, PlaylistMessageInterface, QuestionInterface } from "../../helpers";


interface Props {
  playlistMessage: PlaylistMessageInterface
  sendCommand: (cmd: PendingCommand) => void
}


export const Conditions: React.FC<Props> = props => {

  const [playlistMessage, setPlaylistMessage] = React.useState<PlaylistMessageInterface>(null);
  const [questions, setQuestions] = React.useState<QuestionInterface[]>([]);

  const [questionId, setQuestionId] = React.useState(0);
  const [operator, setOperator] = React.useState("=");
  const [value, setValue] = React.useState("");
  const [dataType, setDataType] = React.useState("bool");
  const [error, setError] = React.useState("");


  const handleChange = (e: any) => {
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "question": setQuestionId(parseInt(val, 0)); break;
      case "operator": setOperator(val); break;
      case "value": setValue(val); break;
      case "dataType": setDataType(val); break;
    }
    setError("");
  }

  // Helper functions for better UX
  const getOperatorText = (op: string) => {
    const operators: { [key: string]: string } = {
      "=": "equals",
      "<>": "does not equal",
      "<": "is less than",
      "<=": "is less than or equal to",
      ">": "is greater than",
      ">=": "is greater than or equal to",
      "contains": "contains",
      "startsWith": "starts with",
      "endsWith": "ends with"
    };
    return operators[op] || op;
  }

  const getDataTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      "bool": "True/False",
      "double": "Number", 
      "string": "Text"
    };
    return types[type] || type;
  }

  const validateCondition = () => {
    if (!questionId || questionId === 0) {
      setError("Please select a question");
      return false;
    }
    if (!value.trim()) {
      setError("Please enter a value");
      return false;
    }
    if (dataType === "double" && isNaN(Number(value))) {
      setError("Please enter a valid number");
      return false;
    }
    return true;
  }



  const handleSave = () => {
    props.sendCommand({ action: "updateConditions", parameter: playlistMessage })
  }


  const init = () => {
    if (props.playlistMessage) {
      setPlaylistMessage(props.playlistMessage);
      let params = new URLSearchParams(window.location.search);
      ApiHelper.get<QuestionInterface[]>("/questions/feed/" + params.get("feedId"))
        .then(data => {
          setQuestions(data || [])
          if ((data || []).length > 0) setQuestionId(data[0].id);
        })
        .catch(err => console.error("Failed to load feed questions for conditions:", err));
    }
  }


  const loadConditions = async () => {
    if (playlistMessage && !playlistMessage.deleteConditionIds) {
      let conditions: PlaylistMessageConditionInterface[] = [];
      if (playlistMessage.id > 0) {
        conditions = await ApiHelper.get<PlaylistMessageConditionInterface[]>("/playlistMessageConditions/playlistMessage/" + playlistMessage.id) || [];
      }
      let pm = { ...playlistMessage };
      pm.conditions = conditions;
      pm.deleteConditionIds = [];
      setPlaylistMessage(pm);
    }
  }

  const handleDelete = (index: number) => {
    let pm = { ...playlistMessage };
    const conditions: PlaylistMessageConditionInterface[] = pm.conditions.splice(index, 1);
    if (conditions[0].id) pm.deleteConditionIds.push(conditions[0].id);
    setPlaylistMessage(pm);
  }

  React.useEffect(init, [props.playlistMessage]);  //eslint-disable-line
  React.useEffect(() => { loadConditions() }, [playlistMessage]);  //eslint-disable-line

  const getDoneButton = () => {
    return <Button size="sm" variant="success" onClick={(e) => { e.preventDefault(); handleSave(); }}>Done</Button>
  }

  const getQuestions = () => {
    const result: React.ReactElement[] = [];
    questions?.forEach(q => {
      result.push(<option value={q.id} key={q.id}>{q.title}</option>);
    })
    return result;
  }

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!validateCondition()) return;
    
    const pm = { ...playlistMessage };
    pm.conditions.push({ questionId, operator, value, dataType });
    setPlaylistMessage(pm);
    
    // Reset form
    setValue("");
    setError("");
  }


  const getRows = () => {
    const result: React.ReactElement[] = [];
    if (playlistMessage?.conditions) {
      let idx = 0;
      for (let c of playlistMessage?.conditions) {
        const q: QuestionInterface = ArrayHelper.getOne(questions, "id", c.questionId);
        if (q) {
          const index = idx;
          const deleteLink = <a href="about:blank" onClick={(e) => { e.preventDefault(); handleDelete(index) }}><i className="far fa-trash-alt text-danger"></i></a>
          result.push(<tr key={index}>
            <td>{q.title}</td>
            <td>{getOperatorText(c.operator)}</td>
            <td>{c.value}</td>
            <td>{getDataTypeText(c.dataType)}</td>
            <td>{deleteLink}</td>
          </tr>)
        }
        idx++;
      }
    }
    return result;
  }

  return (
    <>
      <DisplayBox headerIcon="fas fa-equals" headerText="Conditions" editContent={getDoneButton()}>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Question</th>
              <th>Condition</th>
              <th>Value</th>
              <th>Type</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {getRows()}
            <tr>
              <td>
                <FormControl size="sm" as="select" id="question" value={questionId} onChange={handleChange}>
                  <option value={0}>Select question...</option>
                  {getQuestions()}
                </FormControl>
              </td>
              <td>
                <FormControl size="sm" as="select" id="operator" value={operator} onChange={handleChange}>
                  <option value="=">equals</option>
                  <option value="<>">does not equal</option>
                  <option value="<">is less than</option>
                  <option value="<=">is less than or equal to</option>
                  <option value=">=">is greater than or equal to</option>
                  <option value=">">is greater than</option>
                </FormControl>
              </td>
              <td>
                <FormControl size="sm" id="value" value={value} onChange={handleChange}></FormControl>
              </td>
              <td>
                <FormControl size="sm" as="select" id="dataType" value={dataType} onChange={handleChange}>
                  <option value="bool">True/False</option>
                  <option value="double">Number</option>
                  <option value="string">Text</option>
                </FormControl>
              </td>
              <td>
                <Button size="sm" className="btn btn-success" onClick={handleAdd} >Add</Button>
              </td>
            </tr>
          </tbody>
        </table>
      </DisplayBox>
    </>
  );
};